import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Skeleton } from "primereact/skeleton";
import useToastStore from "@/shared/zustand/toastStore";
import useProjectStore from "@/shared/zustand/projectManagementStore";
import { ArrowUp, Plus, Upload } from "lucide-react";
import { ProjectDetailsDialog } from "@/widgets/projectCreation/components/ProjectDetailsDialog";
import FilePreview from "@/components/FilePreview";
import { ProjectHeader } from "./ProjectHeader";
import sendDataAnimation from "@/assets/animations/sendDataAnimation.json";
// import { v4 as uuidv4 } from "uuid";
import useAuthStore from "@/shared/zustand/authStore";
import { createSubproject, fetchProjectById, fetchSubProjects, manualUploadFilesToGCS, renameFile, validateUserWithProject, } from "@/core/api/api";
import CustomTimeline from "./ProjectFilesTimelineComponent";
import Lottie from "react-lottie-player";
import NotFoundAnimation from "@/assets/animations/notfoundAnimation.json";
import EmptyBox from "@/assets/images/empty-box.svg";
import ProjectSubProjectsList from "./ProjectSubProjectsList";
import { ScrollTop } from "primereact/scrolltop";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ShareProjectDialog from "./ShareProjectDialog";
import FileDeleteConfirmationDialog from "./FileDeleteConfirmationDialog";
const MAX_TOTAL_SIZE_MB = 900; // Maximum total size in MB
const MAX_FILES = 10;
const ProjectPage = () => {
    const { selectedProject, dialogVisible, setShowProjectPage, createSubProject, popFromHistory, navigationHistory, previewDialogVisible, selectedFile, setPreviewDialogVisible, setSelectedFile, setDialogVisible, } = useProjectStore();
    const showToast = useToastStore((state) => state.showToast);
    const { user } = useAuthStore();
    if (!selectedProject) {
        return (_jsxs("div", { className: "w-full flex-1 flex flex-col gap-y-4", children: [_jsx(Lottie, { animationData: NotFoundAnimation, loop: true, play: true, className: "w-full md:w-1/2 mx-auto" }), _jsx("p", { children: "No project selected" })] }));
    }
    const fileInputRef = useRef(null);
    const projectPageRef = useRef(null); // change this ref name
    const [projectfiles, setProjectFiles] = useState([]);
    const [gettingFiles, setGettingFiles] = useState(false);
    const [fileDialogVisible, setFileDialogVisible] = useState(false);
    const [addingFiles, setAddingFiles] = useState(false); // Toggle for file upload UI
    const [subprojectsInfo, setSubprojectsInfo] = useState();
    const [showSubProjectsList, setShowSubProjectsList] = useState(false);
    // sub project creations states
    const [subProjectDialogVisible, setSubProjectDialogVisible] = useState(false);
    const [subProjectName, setSubProjectName] = useState("");
    const [subProjectDescription, setSubProjectDescription] = useState("");
    const [isCreatingSubProject, setIsCreatingSubProject] = useState(false);
    // shareProject dialog states
    const [userHasAccessToProject, setUserHasAccessToProject] = useState(false);
    // console.log("printing userHasAccessToProject", userHasAccessToProject);
    const [shareProjectDialogVisible, setShareProjectDialogVisible] = useState(false);
    // file deletion dialog states
    const [deleteFileDialogVisible, setDeleteFileDialogVisible] = useState(false);
    const [isDeletingFile, setIsDeletingFile] = useState(false);
    useEffect(() => {
        getFiles();
    }, [selectedProject._id, selectedProject.parent]);
    useEffect(() => {
        const fetchInfo = async () => {
            const subprojects = await getSubProjectsInfo(selectedProject._id);
            // console.log("subprojects api: ", subprojects);
            setSubprojectsInfo(subprojects);
        };
        setUserHasAccessToProject(false); // Reset userHasAccessToProject every timme when selectedProject changes
        if (selectedProject?._id) {
            validateUserProjectAccess(selectedProject._id);
        }
        fetchInfo();
    }, [selectedProject._id]);
    const [isUploading, setIsUploading] = useState(false);
    const handleGoBack = () => {
        if (previewDialogVisible) {
            setShowProjectPage(true);
            setPreviewDialogVisible(false);
        }
        else {
            popFromHistory();
            // setShowProjectPage(false);
        }
    };
    const handleCreateSubProject = () => {
        setSubProjectName("");
        setSubProjectDescription("");
        setSubProjectDialogVisible(true);
    };
    const createNewSubProject = async () => {
        if (!user) {
            console.warn("User not logged in");
            return;
        }
        if (subProjectName.length === 0 || !subProjectName.trim()) {
            showToast("warn", "Warning", "Sub project name is required");
            return;
        }
        if (!user || !user.provider.name) {
            console.warn("Dont have user details");
            showToast("warn", "Warn", "Dont have user details");
            return;
        }
        setIsCreatingSubProject(true);
        try {
            const { responseData, error } = await createSubproject(selectedProject._id, [user?.email], subProjectName, user.provider.name, subProjectDescription);
            if (error) {
                console.error("Error creating subproject", error);
                showToast("error", "Error", error.errorMessage);
                return;
            }
            if (responseData?.project_id) {
                const newProject = {
                    _id: responseData.project_id,
                    name: subProjectName,
                    description: subProjectDescription,
                    file_urls: [],
                    parent: selectedProject._id,
                    timestamp: new Date().toISOString(),
                    subProjects: [],
                };
                createSubProject(selectedProject._id, newProject);
                setShowSubProjectsList(false); // reset this to show files by default for subprojects as well
                setSubProjectDialogVisible(false);
                setSubProjectName(""); // resetting subproject name
                setSubProjectDescription(""); // resetting subproject description
                showToast("success", "Success", "Sub project created successfully!");
            }
        }
        catch (error) {
            console.error("Error creating subproject:", error);
            showToast("error", "Error", "Failed to create sub project");
        }
        finally {
            setIsCreatingSubProject(false);
        }
    };
    const validateUserProjectAccess = async (currentProjectId) => {
        if (!user || !user.email) {
            console.warn("User not logged in");
            showToast("warn", "Warn", "User not logged in");
            return;
        }
        if (!currentProjectId) {
            console.warn("currentProjectId not found");
            showToast("warn", "Warn", "currentProjectId not found");
            return;
        }
        const { responseData, error } = await validateUserWithProject(user.email, currentProjectId);
        if (error) {
            console.error("Error validating user project access", error);
            showToast("error", "Error", error.errorMessage);
            return;
        }
        if (responseData && responseData.role && responseData.role.length > 0) {
            console.log("responseData.role[0].role", responseData.role[0].role);
            setUserHasAccessToProject(responseData.role[0].role === "owner" || responseData.role[0].role === "editor");
        }
    };
    const getFiles = async () => {
        setGettingFiles(true);
        // console.log("selectedProject in getFiles: ", selectedProject);
        // Fetch main files and add isEditable: true
        const mainFiles = (await fetchProjectFiles(selectedProject._id)).map((file) => ({
            ...file,
            isEditable: true,
        }));
        // Fetch parent files and add isEditable: false
        const parentFiles = selectedProject.parent
            ? (await fetchProjectFiles(selectedProject.parent)).map((file) => ({
                ...file,
                isEditable: false,
            }))
            : [];
        // console.log("mainFiles: ", mainFiles, "parentFiles:", parentFiles);
        let totalFiles = [...mainFiles, ...parentFiles];
        // Sort files by timestamp in descending order
        totalFiles.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setProjectFiles(totalFiles);
        setGettingFiles(false);
    };
    const handleFileUpload = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) {
            showToast("warn", "Warning", "No files selected.");
            return;
        }
        if (files.length > MAX_FILES) {
            showToast("warn", "Warning", `You can upload a maximum of ${MAX_FILES} files at a time.`);
            return;
        }
        const totalSizeMB = Array.from(files)?.reduce((total, file) => total + file.size / (1024 * 1024), 0);
        if (totalSizeMB > MAX_TOTAL_SIZE_MB) {
            showToast("warn", "Warning", `Total file size exceeds ${MAX_TOTAL_SIZE_MB}MB. Please select smaller files.`);
            return;
        }
        setIsUploading(true);
        // old code for api call
        // const formData = new FormData();
        // formData.append("project_id", selectedProject?._id!!);
        // Array.from(files).forEach((file) => {
        //   formData.append("files", file);
        // });
        // const isAI = false;
        // formData.append("isAI", isAI.toString());
        try {
            // old code for api call
            // await axios.put(
            //   `${process.env.VITE_BASE_API_URL}/gcs/upload-files/`,
            //   formData,
            //   {
            //     headers: {
            //       "Content-Type": "multipart/form-data",
            //     },
            //   }
            // );
            const { responseData, error } = await manualUploadFilesToGCS(selectedProject?._id, // Project ID
            files, // Files array
            false // isAI flag
            );
            if (error) {
                console.error("Error uploading files:", error);
                showToast("error", "Error", error.errorMessage);
                return;
            }
            showToast("success", "Success", "Files uploaded successfully!");
            // fetchProjectFiles(); // Refresh the file list after uploading
            getFiles();
            setAddingFiles(false);
            setFileDialogVisible(false);
        }
        catch (error) {
            console.error("Error uploading files:", error);
            showToast("error", "Error", "Failed to upload files.");
        }
        finally {
            setIsUploading(false);
        }
    };
    const fetchProjectFiles = async (projectId) => {
        try {
            //old ones
            // const response = await axios.get(
            //   `${process.env.VITE_BASE_API_URL}/gcs/listFiles/${projectId}`
            // );
            // old code for api call
            // const response = await axios.get(
            //   `${process.env.VITE_BASE_API_URL}/projects/${projectId}`
            // );
            const { responseData, error } = await fetchProjectById(projectId);
            if (error || !responseData) {
                console.error("Error fetching files for project:", projectId, error, responseData);
                showToast("error", "Error", "Failed to fetch project files. Check console for more details.");
                return [];
            }
            // Ensure conversations is always an array
            const updatedConversations = responseData?.conversations
                ? responseData.conversations.map((conv) => ({
                    ...conv,
                    isHistory: true, // New field added
                }))
                : []; // Fallback to empty array if conversations are missing
            // Merge files and updated conversations, ensuring both are arrays
            const d = [...(responseData?.files || []), ...updatedConversations];
            return d; //response.data.files;
        }
        catch (error) {
            console.error("Error fetching files for project:", projectId, error);
            showToast("error", "Error", "Failed to fetch project files. Check console for more details.");
            return [];
        }
    };
    const getSubProjectsInfo = async (projectId) => {
        const { responseData, error } = await fetchSubProjects(projectId);
        if (error) {
            console.error("Error fetching sub projects info", error);
            return [];
        }
        return responseData?.subprojects;
    };
    const handleEditFileName = async (currentFileName, newFileName) => {
        try {
            // old code for api call
            // const response = await axios.put(
            //   `${process.env.VITE_BASE_API_URL}/gcs/rename-file/`,
            //   {
            //     project_id: selectedProject._id,
            //     current_filename: currentFileName,
            //     new_filename: newFileName,
            //   }
            // );
            const { responseData, error } = await renameFile(selectedProject._id, currentFileName, newFileName);
            if (error) {
                console.error("Error renaming file:", error);
                showToast("error", "Error", error.errorMessage);
                return;
            }
            if (!responseData) {
                console.error("Error renaming file:", responseData);
                showToast("error", "Error", "Failed to rename file");
                return;
            }
            if (responseData) {
                showToast("success", "Success", "File renamed successfully!");
                // fetchProjectfiles();
                getFiles();
            }
        }
        catch (error) {
            console.error("Error renaming file:", error);
            showToast("error", "Error", "Failed to rename file");
        }
    };
    const toggleFileDialog = () => {
        setFileDialogVisible(!fileDialogVisible);
        setAddingFiles(false); // Reset to view mode when toggling
        setIsUploading(false);
    };
    // this function gets the id of the selected project whether it is of a parent or a subproject
    const getProjectIdFromFile = (files, fileName) => {
        const file = files.find((f) => f.name === fileName);
        if (!file || !file.url) {
            return null;
        }
        // URL format: "client-creative-workspace/projects/{projectId}/filename"
        const urlParts = file.url.split("/");
        const projectsIndex = urlParts.findIndex((part) => part === "projects");
        if (projectsIndex === -1 || projectsIndex + 1 >= urlParts.length) {
            return null;
        }
        return urlParts[projectsIndex + 1];
    };
    const handleOpenShareProjectDialog = () => {
        // console.log("printing the selected project to share",selectedProject)
        setShareProjectDialogVisible(true);
    };
    return (_jsxs("div", { className: "p-5 flex flex-col justify-between h-full", ref: projectPageRef, children: [_jsx(ProjectHeader, { projectId: selectedProject._id, initialName: selectedProject.name, onGoBack: handleGoBack, isNewProject: navigationHistory.length > 1, handleCreateSubProject: handleCreateSubProject, showSubProjectsList: showSubProjectsList, setShowSubProjectsList: setShowSubProjectsList, addingFiles: addingFiles, handleOpenShareProjectDialog: handleOpenShareProjectDialog, userHasAccessToProject: userHasAccessToProject }), !previewDialogVisible ? (_jsxs("div", { className: "flex flex-col gap-y-6 h-[calc(100%-90px)]", children: [!showSubProjectsList && (_jsxs("div", { className: "relative h-[calc(100%-0rem)]", children: [_jsxs("div", { className: "grid grid-cols-2 gap-x-2 text-[#344054]", children: [_jsx("span", { className: "text-center p-2 text-lg", children: "Imported Artifacts" }), _jsx("span", { className: "text-center p-2 text-lg", children: "AI generated Artifacts" })] }), _jsxs("div", { className: `pb-10 border border-[#d1d5db] rounded-[6px] px-4 pt-4 transition-all duration-1000 h-[calc(100%-2.75rem)] overflow-y-auto`, children: [gettingFiles ? (_jsx("div", { className: "flex flex-col gap-y-2", children: [1, 2, 3, 4].map((key) => (_jsx(Skeleton, { className: "h-[75px]", height: "75", borderRadius: "8px" }, key))) })) : projectfiles?.length > 0 ? (_jsx(CustomTimeline, { files: projectfiles, addingFiles: addingFiles, setAddingFiles: setAddingFiles, setFileDialogVisible: setFileDialogVisible, userHasAccessToProject: userHasAccessToProject, setDeleteFileDialogVisible: setDeleteFileDialogVisible })) : (_jsxs("div", { className: "w-full h-full flex flex-col items-center justify-center", children: [_jsx("div", { className: "w-[300px]", children: _jsx(EmptyBox, {}) }), _jsx("p", { className: "text-center text-[#374151]", children: "No files to show. Would you like to add files?" }), _jsx(Button, { icon: _jsx(Plus, { size: 16, color: !userHasAccessToProject ? "#D1D5DB" : "#0D9488" }), disabled: !userHasAccessToProject || addingFiles, label: "Add Files", 
                                                // className="w-fit mt-4 text-sm flex items-center gap-x-2 text-[#374151] border border-[#d1d5db] rounded-[6px] px-4 py-2"
                                                className: !userHasAccessToProject ? " pointer-events-auto opacity-70 cursor-not-allowed flex-shrink-0 w-fit mt-4 text-sm flex items-center gap-x-2 text-[#374151] border border-[#d1d5db] rounded-[6px] px-4 py-2" : " flex-shrink-0 w-fit mt-4 text-sm flex items-center gap-x-2 text-[#374151] border border-[#d1d5db] rounded-[6px] px-4 py-2", text: true, onClick: () => {
                                                    setFileDialogVisible(true);
                                                    setAddingFiles(true);
                                                } })] })), _jsx(ScrollTop, { target: "parent", threshold: 100, className: "size-8 bg-[#e0e0e0] *:text-[#000] ml-auto shadow-none absolute", icon: _jsx(ArrowUp, { size: 16 }) })] })] })), showSubProjectsList && (_jsx(ProjectSubProjectsList, { gettingFiles: gettingFiles, subprojectsInfo: subprojectsInfo, setShowSubProjectsList: setShowSubProjectsList, projectName: selectedProject.name }))] })) : (_jsx(FilePreview, { type: "regular", visible: previewDialogVisible, onHide: () => {
                    setPreviewDialogVisible(false);
                    setSelectedFile(null);
                }, fileName: selectedFile?.name ?? "", projectId: selectedFile
                    ? getProjectIdFromFile(projectfiles, selectedFile.name) ??
                        selectedProject?._id
                    : selectedProject?._id })), userHasAccessToProject && (_jsx(Dialog, { header: "Add to project knowledge", visible: fileDialogVisible, onHide: () => {
                    if (isUploading) {
                        showToast("warn", "Warning", "Please wait until file upload is complete");
                    }
                    else {
                        toggleFileDialog();
                    }
                }, closable: true, draggable: false, dismissableMask: !isUploading, headerClassName: "rounded-t-[8px] text-base", contentClassName: "rounded-b-[8px]", appendTo: projectPageRef?.current, className: "max-w-[600px] font-content rounded-[8px] bg-[#fff] shadow-none border", maskClassName: "!absolute !rounded-[8px] backdrop-blur-sm border border-[#d1d5db]", children: _jsxs("div", { className: "flex flex-col gap-y-6", children: [_jsxs(Button, { className: "text-sm w-full text-[#374151] px-6 py-4 rounded-[6px] flex flex-col items-center gap-3 border border-[#EAECF0]", onClick: () => fileInputRef.current?.click(), disabled: isUploading, children: [_jsx("span", { children: isUploading ? (_jsx("i", { className: "pi pi-spin pi-spinner text-xl text-blue-500" })) : (_jsx(Upload, {})) }), _jsx("span", { children: isUploading
                                        ? "Uploading files..."
                                        : `PNG/ JPEG/ PDF/ MP3 formats (max size ${MAX_TOTAL_SIZE_MB} MB)` })] }), _jsx("input", { title: "upload local files", type: "file", multiple: true, ref: fileInputRef, className: "hidden", onChange: handleFileUpload, disabled: isUploading })] }) })), userHasAccessToProject && (_jsx(Dialog, { header: "Sub Project Creation", visible: subProjectDialogVisible, onHide: () => {
                    if (!isCreatingSubProject) {
                        setSubProjectDialogVisible(false);
                    }
                }, closable: !isCreatingSubProject, draggable: false, dismissableMask: !isCreatingSubProject, headerClassName: "rounded-t-[8px] text-base", contentClassName: "rounded-b-[8px]", appendTo: projectPageRef?.current, className: "max-w-[600px] w-[500px] font-content rounded-[8px] bg-[#fff] shadow-none border", maskClassName: "!absolute !rounded-[8px] backdrop-blur-sm border border-[#d1d5db]", footer: !isCreatingSubProject && (_jsxs("div", { className: "flex justify-end gap-x-2", children: [_jsx(Button, { label: "Cancel", className: "border border-[#d1d5db] text-[#374151] px-4 py-2 rounded-[8px]", onClick: () => setSubProjectDialogVisible(false) }), _jsx(Button, { label: "Create", className: `py-2 px-4 transition-all duration-300 ${subProjectName.length > 0
                                ? "bg-gradient-to-r from-[#2546F0] to-[#5928ED] hover:from-[#0039cc] hover:to-[#0039cc] text-white"
                                : "bg-gray-300 text-gray-500"}  rounded-[8px] shadow-sm`, onClick: createNewSubProject, disabled: !subProjectName.trim() })] })), children: isCreatingSubProject ? (_jsxs("div", { className: "w-full h-full m-auto flex flex-col justify-center items-center p-6", children: [_jsx(Lottie, { animationData: sendDataAnimation, loop: true, play: true, className: "w-full md:w-1/2 mx-auto" }), _jsx("p", { className: "text-lg font-heading", children: "Creating sub project..." })] })) : (_jsxs("div", { className: "flex flex-col gap-y-4", children: [_jsxs("div", { className: "flex flex-col gap-y-2", children: [_jsxs("label", { htmlFor: "subProjectName", className: "text-sm text-[#344054]", children: ["Sub Project Name ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx(InputText, { id: "subProjectName", value: subProjectName, onChange: (e) => setSubProjectName(e.target.value), className: "p-2 border border-[#d1d5db] rounded-[6px]", placeholder: "Enter sub project name" })] }), _jsxs("div", { className: "flex flex-col gap-y-2", children: [_jsx("label", { htmlFor: "subProjectDescription", className: "text-sm text-[#344054]", children: "Sub Project Description" }), _jsx(InputTextarea, { id: "subProjectDescription", value: subProjectDescription, onChange: (e) => setSubProjectDescription(e.target.value), rows: 4, className: "p-2 border border-[#d1d5db] rounded-[6px] resize-none", placeholder: "Enter sub project description (optional)" })] })] })) })), userHasAccessToProject && shareProjectDialogVisible && selectedProject && (_jsx(ShareProjectDialog, { visible: shareProjectDialogVisible, onHide: () => setShareProjectDialogVisible(false), selectedProjectToShare: selectedProject, parentRef: projectPageRef })), userHasAccessToProject && dialogVisible && selectedFile && (_jsx(ProjectDetailsDialog, { type: "edit", visible: dialogVisible, onHide: () => {
                    setDialogVisible(false);
                    setSelectedFile(null);
                }, parentRef: projectPageRef, projectId: selectedProject?._id, currentFileName: selectedFile?.name, onConfirm: handleEditFileName })), userHasAccessToProject && deleteFileDialogVisible && selectedFile && (_jsx(FileDeleteConfirmationDialog, { visible: deleteFileDialogVisible, onHide: () => {
                    setSelectedFile(null);
                    setDeleteFileDialogVisible(false);
                }, projectId: selectedProject?._id, selectedFile: selectedFile, parentRef: projectPageRef, onDeleteSuccess: () => {
                    getFiles(); // Refresh the file list
                    setSelectedFile(null);
                } }))] }));
};
export default ProjectPage;
